import Account from "@/models/account";
import { HostLogin } from "@/models/hostLogin";
import { getAllAccounts, getAllHostLogins } from "@/services/account.service";
import {
  Module,
  ActionTree,
  MutationTree,
  GetterTree,
  ActionContext,
} from "vuex";
import { RootState } from "..";

export interface AccountStoreState {
  accounts: Account[];
  loadingAccounts: boolean;
  errorLoadingAccounts: boolean;

  hostLogins: HostLogin[];
  loadingHostLogins: boolean;
  errorLoadingHostLogins: boolean;

  search: string | null;
  includeBlockedAccounts: boolean;
}

const state: AccountStoreState = {
  accounts: [],
  loadingAccounts: false,
  errorLoadingAccounts: false,

  hostLogins: [],
  loadingHostLogins: false,
  errorLoadingHostLogins: false,

  search: null,
  includeBlockedAccounts: false,
};

interface AccountStoreGetters extends GetterTree<AccountStoreState, RootState> {
  accounts: (state: AccountStoreState) => Account[];
  loadingAccounts: (state: AccountStoreState) => boolean;
  errorLoadingAccounts: (state: AccountStoreState) => boolean;

  hostLogins: (state: AccountStoreState) => HostLogin[];
  loadingHostLogins: (state: AccountStoreState) => boolean;
  errorLoadingHostLogins: (state: AccountStoreState) => boolean;

  search: (state: AccountStoreState) => string | null;
  includeBlockedAccounts: (state: AccountStoreState) => boolean;
}

const getters: AccountStoreGetters = {
  accounts: (state) => state.accounts,
  loadingAccounts: (state) => state.loadingAccounts,
  errorLoadingAccounts: (state) => state.errorLoadingAccounts,

  hostLogins: (state) => state.hostLogins,
  loadingHostLogins: (state) => state.loadingHostLogins,
  errorLoadingHostLogins: (state) => state.errorLoadingHostLogins,

  search: (state) => state.search,
  includeBlockedAccounts: (state) => state.includeBlockedAccounts,
};

interface AccountStoreActions extends ActionTree<AccountStoreState, RootState> {
  loadAccounts(
    context: ActionContext<AccountStoreState, RootState>
  ): Promise<void>;
  loadHostLogins(
    context: ActionContext<AccountStoreState, RootState>
  ): Promise<void>;
}

const actions: AccountStoreActions = {
  async loadAccounts(context) {
    try {
      context.commit("setErrorLoadingAccounts", false);
      context.commit("setLoadingAccounts", true);
      const response = await getAllAccounts();
      const accounts = response.data.map((dto) => new Account(dto));

      context.commit("setAccounts", accounts);
    } catch (error) {
      context.commit("setErrorLoadingAccounts", true);
      throw error;
    } finally {
      context.commit("setLoadingAccounts", false);
    }
  },
  async loadHostLogins(context) {
    try {
      context.commit("setErrorLoadingHostLogins", false);
      context.commit("setLoadingHostLogins", true);
      const response = await getAllHostLogins();
      const hostLogins = response.data.map((dto) => new HostLogin(dto));

      context.commit("setHostLogins", hostLogins);
    } catch (error) {
      context.commit("setErrorLoadingHostLogins", true);
      throw error;
    } finally {
      context.commit("setLoadingHostLogins", false);
    }
  },
};

interface AccountStoreMutations extends MutationTree<AccountStoreState> {
  setAccounts(state: AccountStoreState, accounts: Account[]): void;
  setLoadingAccounts(state: AccountStoreState, loadAccounts: boolean): void;
  setErrorLoadingAccounts(
    state: AccountStoreState,
    errorLoadingAccounts: boolean
  ): void;

  setHostLogins(state: AccountStoreState, hostLogins: HostLogin[]): void;
  setLoadingHostLogins(
    state: AccountStoreState,
    isLoadingHostLogins: boolean
  ): void;
  setErrorLoadingHostLogins(
    state: AccountStoreState,
    errorLoadingHostLogins: boolean
  ): void;

  setSearch(state: AccountStoreState, search: string | null): void;
  setIncludeBlockedAccounts(
    state: AccountStoreState,
    includeBlockedAccounts: boolean
  ): void;
}

const mutations: AccountStoreMutations = {
  setAccounts(state, accounts: Account[]): void {
    state.accounts = accounts;
  },
  setLoadingAccounts(state, loadingAccounts) {
    state.loadingAccounts = loadingAccounts;
  },
  setErrorLoadingAccounts(state, errorLoadingAccounts) {
    state.errorLoadingAccounts = errorLoadingAccounts;
  },

  setHostLogins(state, hostLogins: HostLogin[]): void {
    state.hostLogins = hostLogins;
  },
  setLoadingHostLogins(state, isLoadingHostLogins) {
    state.loadingHostLogins = isLoadingHostLogins;
  },
  setErrorLoadingHostLogins(state, errorLoadingHostLogins) {
    state.errorLoadingHostLogins = errorLoadingHostLogins;
  },

  setSearch(state, search) {
    state.search = search;
  },
  setIncludeBlockedAccounts(state, includeBlockedAccounts) {
    state.includeBlockedAccounts = includeBlockedAccounts;
  },
};

export interface AccountStore extends Module<AccountStoreState, RootState> {
  namespaced: true;
  state?: AccountStoreState;
  getters: AccountStoreGetters;
  actions: AccountStoreActions;
  mutations?: AccountStoreMutations;
}

const accountModule: AccountStore = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default accountModule;
